import { FormArray, FormGroup, Validators } from '@angular/forms';
import { GarnishmentProcessingType } from '@safarilaw-webapp/feature/co-manage/data-access';
import { AccountExemptionType, AccountLocationType, ExemptionTrackingState, Garnishment, GarnishmentStatus, PayeeIdentity } from '@safarilaw-webapp/feature/lpms/data-access';
import { MixinConstructor } from '@safarilaw-webapp/shared/common-objects-models';

import { AccordionControlService } from '@safarilaw-webapp/shared/ui-kit/accordion';
import { FormHelper } from '@safarilaw-webapp/shared/ui-kit/form-utility';
import { SafariValidators } from '@safarilaw-webapp/shared/ui-kit/safari-form';
import { Subscription, distinctUntilChanged, map } from 'rxjs';
import { InfoRequestFormHelper } from '../../../../../../helpers/info-request-form-helper';
import { LpmsGarnishmentEditPageUiReduxObject } from '../../../../../garnishment/garnishment-edit/redux/state';
import { MatterEditFormComponent } from '../matter-edit-form.component';

export class GarnishmentFinancialsInfo {
  // These are manually entered values
  /**
   * levyOrderAmount - total amount of the levy order
   */
  levyOrderAmount: number;
  /**
   * releaseAmount - Manually entered release amount (does not take ledgers into account)
   */
  releaseAmount: number;
  // These are calculated totals based on totals of all accounts + ledgers
  //////////////////////////////////
  /**
   * totalPaymentAmount = sum of all payments
   */
  totalPaymentAmount: number;
  /**
   * totalPendingAmount = levyOrderAmount - releaseAmount (manually entered, not from ledgers) - totalPaymentAmount
   */
  totalPendingAmount: number;
  /**
   * totalSeized = sum of all seized funds ONLY (does not include releases and payments)
   */
  totalSeizedFundsAmount: number;
  /**
   * totalReleasedAmount = sum of all released amounts
   */
  totalReleasedAmount: number;
  /**
   * totalProcessingFeeAmount = sum of all processing fees
   */
  totalProcessingFeeAmount: number;
  /**
   * maxProcessingFeeAmount = Comes from company settings but we also keep it here for easier access
   */
  maxProcessingFeeAmount: number;
  /**
   * remainingAllowedProcessingFeeAmount = Comes from company settings - sum of all processing fees but we also keep it here for easier access
   */
  remainingAllowedProcessingFeeAmount: number;
  /**
   * maxProcessingFeeExceededMessage = A message used by validators to indicate that the maximum processing fee has been exceeded
   */
  maxProcessingFeeExceededMessage: string;
  /**
   * maxSeizableAmount = totalPendingAmount plus all released amount in all ledgers minus seized amount in all ledgers (and new accounts about to be saved)
   */
  maxSeizableAmount: number;

  /**
   * currentlySeizedAmount = seized amount in all ledgers (and new accounts about to be saved) minus released amount (including account releases about to be saved) in all ledgers and payments
   */
  currentlySeizedAmount: number;
  /**
   * totalNetSeizedForAllAccounts seized amount in all ledgers (and new accounts about to be saved) minus released amount in all ledgers BUT does not include payments
   */
  totalNetSeizedForAllAccounts: number;
  // These are calculated totals for each account
  /**
   * totalSeizedFundsAmountForAccount - seized funds amount total for each account (does not include releases)
   */
  totalSeizedFundsAmountForAccount: Map<string, number>;
  /**
   * totalReleasedAmountForAccount - released funds amount total for each account
   */

  totalReleasedAmountForAccount: Map<string, number>;
  /**
   * totalProcessingFeeAmountForAccount - processing fee amount total for each account
   */
  totalProcessingFeeAmountForAccount: Map<string, number>;
  /**
   * maxReleasableAmountForAccount - total seized minus total released for each account
   */
  maxReleasableAmountForAccount: Map<string, number>;
}
export const garnishmentFinancialsMixin = <T extends Garnishment, B extends MixinConstructor<MatterEditFormComponent<T>>>(Base: B) =>
  class GarnishmentFinancialsMixin extends Base {
    protected _accordionControlService: AccordionControlService;
    protected _document: Document;

    accountSubscriptions: Map<FormGroup, Subscription[]> = new Map<FormGroup, Subscription[]>();

    get accounts() {
      return this.form.get('accounts') as FormArray;
    }
    get payments() {
      return this.form.get('payments') as FormArray;
    }

    get shouldShowGarnishmentFinancialsSection() {
      return this.currentCompany != null && this.garnishmentFinancialsInfo != null;
    }
    get shouldShowGarnishmentCreditorSection() {
      return true;
    }
    get shouldShowGarnishmentCreditorPayeeSection() {
      return this.form?.get('payeeIdentity')?.value == PayeeIdentity.OtherParty;
    }

    get hasCreditorAddress(): boolean {
      return FormHelper.addressHasUserProvidedValue(this.appModel.creditorAddress);
    }
    get hasPayeeAddress(): boolean {
      return FormHelper.addressHasUserProvidedValue(this.appModel.payeeAddress);
    }

    getDefaultAccountLocationState(): number {
      if (this.subType?.accountLocationType == AccountLocationType.SpecificState) {
        return this.subType.accountLocationState;
      } else if (this.subType?.accountLocationType == AccountLocationType.DebtorState) {
        return this.getDefaultStateFromSubjects();
      }
      return null;
    }

    getDefaultAccountExemptionState(): number {
      if (this.subType?.defaultExemptionTrackingState == ExemptionTrackingState.DebtorState) {
        return this.getDefaultStateFromSubjects();
      } else if (this.subType?.defaultExemptionTrackingState == ExemptionTrackingState.AccountLocationState) {
        const states = this.accounts.controls
          .map(o => o.get('locationState').value)
          .filter(o => o)
          .distinct();
        if (states.length === 1) {
          return states[0];
        }
      } else if (this.subType?.defaultExemptionTrackingState == ExemptionTrackingState.IssuingAuthority) {
        return this.form.get('issuingAuthorityAddress.state').value;
      }
      return null;
    }

    getDefaultStateFromSubjects(): number {
      const states = this.subjects.controls
        .map(o => o.get('address.state').value)
        .filter(o => o)
        .distinct();
      if (states.length === 1) {
        return states[0];
      }
      return null;
    }

    protected setupAddAccountsAndPaymentsSubscription() {
      super.subscribe(this.observeMessage$((this.messages as LpmsGarnishmentEditPageUiReduxObject).requestAddAccount, true).pipe(map(x => x.garnishmentAccount)), o => {
        const accountFormGroup = InfoRequestFormHelper.buildAccount(
          { ...o, locationState: this.getDefaultAccountLocationState() },
          this.formBuilder,
          this.subType?.accountLocationEnabled,
          this.subType?.accountLocationType,
          this.subType?.enableExemptionTrackingFields,
          this.appModel.processingType,
          this.appModel.status as GarnishmentStatus,
          this.isX9,
          this.currentCompany.enableGarnishmentAutoResponse
        );
        accountFormGroup.controls['__isNew'].setValue(true);
        if (this.appModel.processingType != GarnishmentProcessingType.Standard) {
          accountFormGroup.get('number').addValidators(SafariValidators.noControlValueCollisionInArray('number', 'Account number must be unique!'));
          if (this.appModel.processingType == GarnishmentProcessingType.Ledger_Automated) {
            accountFormGroup.get('enableAutomatedTransfer').enable();
          }
        }

        this.addAccountSubscriptions(accountFormGroup);
        this.accounts.push(accountFormGroup);
        const uuid: string = accountFormGroup.controls['uuid'].value;
        const accordionId = 'twisty_accordion' + uuid + '-account';
        // We must delay this message. Our default logic for financial accordions is to show them as collapsed
        // (they have defaultExpanded=false). We probably do that so that when the page loads the first time
        // we want to show them collapsed. BUT in this case we want to expand it. However, if we don't delay
        // accordion expand message the accordion will get created AFTER this message is dispatched, meaning
        // it will never hear it, and will get created in collapsed state as its' default binding asks it to
        setTimeout(() => this._accordionControlService.expandAccordion(accordionId));
        // And this one is for the focus to work. It takes a bit for accordion expand animation to be done
        setTimeout(() => {
          const accountNumberDOMElement = this._document.getElementById(accordionId + '-account-number');
          accountNumberDOMElement.focus();
        }, 150);
      });
      super.subscribe(this.observeMessage$((this.messages as LpmsGarnishmentEditPageUiReduxObject).requestAddPayment, true).pipe(map(x => x.garnishmentPayment)), o => {
        const paymentFormGroup = InfoRequestFormHelper.buildPayment(o, this.formBuilder);
        this.payments.push(paymentFormGroup);
        const uuid: string = paymentFormGroup.controls['uuid'].value;
        const accordionId = 'twisty_accordion' + uuid + '-payment';
        // We must delay this message. Our default logic for financial accordions is to show them as collapsed
        // (they have defaultExpanded=false). We probably do that so that when the page loads the first time
        // we want to show them collapsed. BUT in this case we want to expand it. However, if we don't delay
        // accordion expand message the accordion will get created AFTER this message is dispatched, meaning
        // it will never hear it, and will get created in collapsed state as its' default binding asks it to
        setTimeout(() => this._accordionControlService.expandAccordion(accordionId));
        // And this one is for the focus to work. It takes a bit for accordion expand animation to be done
        setTimeout(() => {
          const accountNumberDOMElement = this._document.getElementById(accordionId + '-account-number');
          accountNumberDOMElement.focus();
        }, 150);
      });
      super.subscribe(this.observeMessage$((this.messages as LpmsGarnishmentEditPageUiReduxObject).requestExpandAccount, true), o => {
        this.expandAccordion(o.id);
      });
    }
    protected setupFinancialSectionSubscription() {
      this.subscribe(this.form.get('releaseAmount').valueChanges.pipe(distinctUntilChanged()), value => {
        if (value) {
          this.form.get('releaseDate').setValidators([Validators.required]);
        } else {
          this.form.get('releaseDate').setValidators([]);
        }
        this.form.get('releaseDate').updateValueAndValidity();
      });
    }

    addAccountSubscriptions(accountFormGroup: FormGroup) {
      this.accountSubscriptions.set(accountFormGroup, [
        accountFormGroup.get('exemptionType').valueChanges.subscribe(exemptionType => {
          const exemptionStateLawControl = accountFormGroup.get('exemptionStateLaw');
          if (this.doesExemptionStateLawApply(exemptionType)) {
            exemptionStateLawControl.addValidators(Validators.required);
            if (exemptionStateLawControl.disabled && !exemptionStateLawControl.value) {
              exemptionStateLawControl.setValue(this.getDefaultAccountExemptionState());
            }
            exemptionStateLawControl.enable();
          } else {
            exemptionStateLawControl.removeValidators(Validators.required);
            exemptionStateLawControl.setValue(null);
            exemptionStateLawControl.disable();
          }

          exemptionStateLawControl.updateValueAndValidity();

          const exemptionNotesControl = accountFormGroup.get('exemptionNotes');
          const exemptionAmountControl = accountFormGroup.get('exemptionAmount');
          if (exemptionType && exemptionType != AccountExemptionType.None) {
            exemptionNotesControl.addValidators(Validators.required);
            exemptionAmountControl.addValidators(Validators.required);
          } else {
            exemptionNotesControl.removeValidators(Validators.required);
            exemptionAmountControl.removeValidators(Validators.required);
            exemptionAmountControl.setValue(null);
          }
          exemptionNotesControl.updateValueAndValidity();
          exemptionAmountControl.updateValueAndValidity();
        })
      ]);
    }

    clearAccountSubscriptions(account: FormGroup) {
      const accountSubscriptions = this.accountSubscriptions.get(account);
      if (!accountSubscriptions) {
        return;
      }
      for (const sub of accountSubscriptions) {
        sub.unsubscribe();
      }
      this.accountSubscriptions.delete(account);
    }
    clearAllAccountSubscriptions() {
      for (const accountSubscriptions of this.accountSubscriptions.values()) {
        for (const sub of accountSubscriptions) {
          sub.unsubscribe();
        }
      }
    }

    doesExemptionStateLawApply(exemptionType: AccountExemptionType): boolean {
      // DUPE: Exemption State Law Applies
      return exemptionType == AccountExemptionType.State || exemptionType == AccountExemptionType.FederalAndState;
    }
    override ngOnDestroy(): void {
      super.ngOnDestroy();
      this.clearAllAccountSubscriptions();
    }
  };
